import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  MapPinIcon,
  CalendarIcon,
  ClockIcon,
  TicketIcon,
  HeartIcon,
  ShareIcon,
  UserIcon,
  PencilIcon,
  FlagIcon,
  HandThumbUpIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIconFilled, HandThumbUpIcon as HandThumbUpSolidIcon } from '@heroicons/react/24/solid';
import { format } from 'date-fns';
import apiService from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import StatusBadge from './StatusBadge';
import ReportModal from './ReportModal';

const Card = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  overflow: hidden;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const CardHeader = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const BandName = styled.div`
  font-size: ${props => props.theme.typography.h4.fontSize};
  font-weight: ${props => props.theme.typography.h4.fontWeight};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.sm};
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  div {
    line-height: 1.2;
  }
`;

const VenueInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.caption.fontSize};
  margin-bottom: ${props => props.theme.spacing.sm};
  
  svg {
    width: 14.4px; /* 16px * 0.9 = 14.4px (10% smaller) */
    height: 14.4px;
  }
`;

const DateTimeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const DateTimeLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
`;

const DateTimeChip = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background: ${props => props.theme.colors.surfaceVariant};
  color: ${props => props.theme.colors.text.secondary};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.small.fontSize};
  
  svg {
    width: 12px;
    height: 12px;
    color: ${props => props.theme.colors.primary};
  }
`;

const GenresContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.xs};
  align-items: center;
`;

const GenreChip = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.small.fontSize};
  font-weight: 500;
  white-space: nowrap;
`;

const PosterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.muted};
  font-size: ${props => props.theme.typography.caption.fontSize};
  margin-top: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const PosterDetails = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  flex-wrap: wrap;
`;

const ScenePoints = styled.span`
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  font-size: ${props => props.theme.typography.small.fontSize};
`;

const CardBody = styled.div`
  padding: ${props => props.theme.spacing.lg};
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.md};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.small.fontSize};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.surfaceVariant};
    color: ${props => props.theme.colors.primary};
  }
  
  &.liked {
    color: ${props => props.theme.colors.error};
    
    svg {
      color: ${props => props.theme.colors.error};
    }
  }
  
  &.thanked {
    color: ${props => props.theme.colors.success};
    
    svg {
      color: ${props => props.theme.colors.success};
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.body.fontSize};
  margin-bottom: ${props => props.theme.spacing.sm};
  line-height: 1.5;
  
  svg {
    width: 16px;
    height: 16px;
    color: ${props => props.theme.colors.primary};
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const DetailContent = styled.div`
  flex: 1;
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin-right: ${props => props.theme.spacing.xs};
`;

const TicketButton = styled.a`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background: ${props => props.theme.colors.primary};
  color: white;
  text-decoration: none;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.small.fontSize};
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primaryDark};
    transform: translateY(-1px);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ShowCard = ({ show, onUpdate, currentUser, showEditButton = false }) => {
  const { isAuthenticated, user: authUser } = useAuth();
  const [isLiked, setIsLiked] = useState(show.is_liked || false);
  const [isThanked, setIsThanked] = useState(show.is_thanked || false);
  const [loading, setLoading] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const navigate = useNavigate();

  // Use the user from AuthContext if currentUser prop is not provided
  const userToUse = currentUser || authUser;

  // Safe access to show properties with fallbacks
  const {
    id,
    band_name,
    venue_name,
    venue_address,
    city,
    state,
    show_date,
    show_time,
    description,
    genre,
    ticket_price,
    ticket_url,
    age_limit,
    poster,
    posterUsername,
  } = show;

  // Check if current user is the creator of this show
  const isCreator = userToUse && (poster?.uid === userToUse.id || poster?.id === userToUse.id);

  const handleLike = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to save shows');
      return;
    }

    const newLikedState = !isLiked;
    // Optimistically update the local state
    setIsLiked(newLikedState);
    setLoading(true);

    try {
      if (newLikedState) {
        await apiService.likeShow(id);
        toast.success('Show saved!');
      } else {
        await apiService.unlikeShow(id);
        toast.success('Removed from saved shows');
      }

      // Notify the parent component of the change without causing a full reload
      if (onUpdate) {
        onUpdate({ ...show, is_liked: newLikedState });
      }
    } catch (error) {
      // Revert the state if the API call fails
      setIsLiked(!newLikedState);
      console.error('Error toggling save:', error);
      toast.error('Failed to update show');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    try {
      // Create a more detailed share text with show information
      const shareText = `${band_name} at ${venue_name}\n` +
        `${formatDate(show_date)}${show_time ? ` at ${formatTime(show_time)}` : ''}\n` +
        `${city}, ${state}\n` +
        `${ticket_price ? `Tickets: ${formatPrice(ticket_price)}` : 'Free show'}\n` +
        `\nCheck it out on SceneFeed!`;

      // Use the main site URL
      const shareUrl = window.location.origin;

      const shareData = {
        title: `${band_name} at ${venue_name}`,
        text: shareText,
        url: shareUrl
      };

      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy the share text and URL to clipboard
        const clipboardText = `${shareText}\n\n${shareUrl}`;
        await navigator.clipboard.writeText(clipboardText);
        toast.success('Show details copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to share');
    }
  };

  const handleEdit = () => {
    navigate(`/edit-show/${id}`);
  };

  const handleThankYou = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to thank show posters');
      return;
    }
    if (isCreator) {
      toast.error('You cannot thank your own shows');
      return;
    }
    if (poster?.user_type !== 'fan') {
      toast.error('You can only thank fan posts');
      return;
    }
    if (isThanked) {
      toast.error('You have already thanked this show');
      return;
    }

    // Optimistic UI update
    const newThankedState = !isThanked;
    setIsThanked(newThankedState);
    setLoading(true);

    try {
      const result = await apiService.thankShow(id, userToUse.id);
      
      if (result.success) {
        toast.success('Thank you! The poster earned 2 scene points 🎵');
        if (onUpdate) {
          onUpdate({ ...show, is_thanked: newThankedState, thank_you_count: (show.thank_you_count || 0) + 1 });
        }
      } else {
        // Revert on failure
        setIsThanked(!newThankedState);
        toast.error(result.error || 'Failed to thank show');
      }
    } catch (error) {
      // Revert on failure
      setIsThanked(!newThankedState);
      console.error('Error thanking show:', error);
      toast.error('Failed to thank show');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    try {
      return format(new Date(date), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    try {
      return format(new Date(`2000-01-01T${time}`), 'h:mm a');
    } catch {
      return time;
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'Free';
    
    // If it's already formatted, return as is
    if (typeof price === 'string' && (price.includes('$') || price.toLowerCase().includes('free'))) {
      return price;
    }
    
    // Try to parse as number and format
    const numPrice = parseFloat(price);
    if (!isNaN(numPrice)) {
      return `$${numPrice.toFixed(2)}`;
    }
    
    // Return as string if can't parse
    return price;
  };

  const cleanDescription = (desc) => {
    if (!desc) return '';
    
    // Remove genre tags like [GENRES:Rock,Hardcore]
    return desc.replace(/\[GENRES:[^\]]+\]/g, '').trim();
  };

  const handleReport = async (reportData) => {
    if (!isAuthenticated) {
      toast.error('Please log in to report shows');
      return;
    }

    setLoading(true);
    try {
      // Map frontend categories to database categories
      const categoryMap = {
        'spam': 'spam',
        'inappropriate': 'inappropriate_content',
        'wrong-info': 'wrong_information',
        'scam': 'scam',
        'harassment': 'harassment',
        'other': 'other'
      };

      const result = await apiService.reportShow(
        reportData.showId,
        userToUse.id,
        categoryMap[reportData.reason],
        reportData.details
      );

      if (result.success) {
        toast.success('Report submitted successfully');
        // Update the show data to reflect the report
        show.report_count = (show.report_count || 0) + 1;
        if (onUpdate) onUpdate();
      } else {
        toast.error(result.error || 'Failed to submit report');
      }
    } catch (error) {
      console.error('Error reporting show:', error);
      toast.error('Failed to submit report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <CardHeader>
        <BandName>
          {band_name
            .split(/,| & /)
            .map(band => band.trim())
            .filter(Boolean)
            .map((band, index) => (
              <div
                key={index}
                style={{
                  color: index % 2 === 0
                    ? (typeof window !== 'undefined' && window.theme?.colors?.primary) || '#8B5CF6'
                    : (typeof window !== 'undefined' && window.theme?.colors?.secondary) || '#3B82F6'
                }}
              >
                {band}
              </div>
            ))}
        </BandName>
        
        <VenueInfo>
          <MapPinIcon />
          <span>{venue_name}</span>
          <span>• {city}, {state}</span>
        </VenueInfo>

        <DateTimeRow>
          <DateTimeLeft>
            <DateTimeChip>
              <CalendarIcon />
              {formatDate(show_date)}
            </DateTimeChip>
            {show_time && (
              <DateTimeChip>
                <ClockIcon />
                {formatTime(show_time)}
              </DateTimeChip>
            )}
          </DateTimeLeft>
          <GenresContainer>
            {(() => {
              // Handle different genre formats
              let genres = [];
              
              // First, try to extract genres from description
              if (description) {
                const genreMatch = description.match(/\[GENRES:(.*?)\]/);
                if (genreMatch) {
                  genres = genreMatch[1].split(',').map(g => g.trim()).filter(g => g.length > 0);
                }
              }
              
              // If no genres found in description, fall back to genre field
              if (genres.length === 0) {
                if (Array.isArray(genre)) {
                  genres = genre;
                } else if (typeof genre === 'string') {
                  // Split by comma, semicolon, or '&' and clean up
                  genres = genre
                    .split(/[,;&]/)
                    .map(g => g.trim())
                    .filter(g => g.length > 0);
                } else {
                  genres = [genre || 'other'];
                }
              }
              
              // Limit to first 3 genres to avoid overcrowding
              return genres.slice(0, 3).map((g, index) => (
                <GenreChip key={index}>
                  {g.charAt(0).toUpperCase() + g.slice(1)}
                </GenreChip>
              ));
            })()}
          </GenresContainer>
        </DateTimeRow>

        <PosterInfo>
          <UserIcon />
          <PosterDetails>
            <span>Posted by <strong>{poster?.username || posterUsername || 'Unknown User'}</strong></span>
            {poster?.user_level && (
              <div style={{
                fontSize: '16px',
                marginLeft: '4px'
              }}>
                {(() => {
                  switch (poster.user_level) {
                    case 'poser': return '🎭';
                    case 'scene_supporter': return '🎵';
                    case 'active_member': return '🎸';
                    case 'trusted_poster': return '⭐';
                    case 'scene_insider': return '🎪';
                    case 'scene_veteran': return '🔥';
                    case 'scene_legend': return '👑';
                    default: return '🎭';
                  }
                })()}
              </div>
            )}
            {poster?.user_level && (
              <StatusBadge level={poster.user_level} showIcon={false} />
            )}
            {poster?.scene_points !== undefined && (
              <ScenePoints>
                {poster.scene_points} pts
              </ScenePoints>
            )}
          </PosterDetails>
        </PosterInfo>
      </CardHeader>

      <CardBody>
        {(() => {
          const hasDescription = description && cleanDescription(description).length > 0;
          return hasDescription ? (
            <DetailItem>
              <DetailContent>
                <DetailLabel>Description:</DetailLabel>
                {cleanDescription(description)}
              </DetailContent>
            </DetailItem>
          ) : null;
        })()}
        
        {age_limit && (
          <DetailItem>
            <DetailContent>
              <DetailLabel>Age Limit:</DetailLabel>
              {age_limit}
            </DetailContent>
          </DetailItem>
        )}
        
        {ticket_price && (
          <DetailItem>
            <DetailContent>
              <DetailLabel>Ticket Price:</DetailLabel>
              {formatPrice(ticket_price)}
            </DetailContent>
          </DetailItem>
        )}
        
        {venue_address && (
          <DetailItem>
            <MapPinIcon />
            <DetailContent>
              <DetailLabel>Address:</DetailLabel>
              {venue_address}
            </DetailContent>
          </DetailItem>
        )}

        <ActionButtons>
          <ActionButton
            onClick={handleLike}
            disabled={loading}
            className={isLiked ? 'liked' : ''}
          >
            {isLiked ? <HeartSolidIconFilled /> : <HeartIcon />}
            {isLiked ? 'Saved' : 'Save Show'}
          </ActionButton>

          <ActionButton onClick={handleShare}>
            <ShareIcon />
            Share
          </ActionButton>

          {!isCreator && userToUse?.user_type === 'fan' && poster?.user_type === 'fan' && (
            <ActionButton 
              onClick={handleThankYou} 
              disabled={loading}
              className={isThanked ? 'thanked' : ''}
            >
              {isThanked ? <HandThumbUpSolidIcon /> : <HandThumbUpIcon />}
              {isThanked ? 'Thanked' : 'Thank'}
            </ActionButton>
          )}

          {isCreator && showEditButton && (
            <ActionButton onClick={handleEdit}>
              <PencilIcon />
              Edit
            </ActionButton>
          )}

          {/* Report button - show for all users except creator */}
          {!isCreator && (
            <ActionButton 
              onClick={() => setShowReportModal(true)}
              disabled={loading}
              style={{ color: '#EF4444' }}
            >
              <FlagIcon />
              Report
            </ActionButton>
          )}

          {ticket_url && (
            <TicketButton href={ticket_url} target="_blank" rel="noopener noreferrer">
              <TicketIcon />
              Get Tickets
            </TicketButton>
          )}
        </ActionButtons>
      </CardBody>

      {/* Report Modal */}
      <ReportModal
        show={show}
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onSubmit={handleReport}
      />
    </Card>
  );
};

export default ShowCard; 